import React from "react"
import { flatten } from "ramda"
import {
  ContentfulProduct,
  ContentfulProductVariant,
} from "../../graphql-types"

export default function useVariantHandler({
  speeds = [],
  defaultValue,
}: {
  speeds: ContentfulProduct[]
  defaultValue?: { speed?: string | null; color?: string | null }
}) {
  const variants = flatten(
    speeds.map(
      (speed) =>
        speed?.variants?.map((variant) => ({ ...variant, speed })) || []
    )
  ).filter(
    (variant) => !!variant && variant["shopifyId"]
  ) as (ContentfulProductVariant & {
    speed: ContentfulProduct
  })[]

  const getDefaultVariant = () => {
    if (!defaultValue) return variants[0]

    const { color, speed } = defaultValue
    if (color && speed) {
      return (
        variants.find(
          (variant) =>
            variant.color?.internalTitle === color &&
            variant.speed.speed === speed
        ) || variants[0]
      )
    } else if (speed) {
      return (
        variants.find((variant) => variant.speed.speed === speed) || variants[0]
      )
    } else {
      return (
        variants.find((variant) => variant.color?.internalTitle === color) ||
        variants[0]
      )
    }
  }

  const [currentVariant, setCurrentVariant] = React.useState(getDefaultVariant)
  const speed = currentVariant.speed.speed
  const color = currentVariant.color?.name

  const onColorChange = (colorInternalTitle: string) => {
    const variantsWithSameColor = variants.filter(
      (variant) => variant.color?.internalTitle === colorInternalTitle
    )
    const colorWithSameSpeed = variantsWithSameColor.find(
      (variant) => variant?.speed?.speed === speed
    )

    if (colorWithSameSpeed) {
      setCurrentVariant(colorWithSameSpeed)
    } else {
      setCurrentVariant(variantsWithSameColor[0])
    }
  }

  const onSpeedChange = (speed: string) => {
    const variantsWithSameSpeed = variants.filter(
      (variant) => variant.speed.speed === speed
    )
    const speedWithSameColor = variantsWithSameSpeed.find(
      (variant) => variant?.color?.name === color
    )

    if (speedWithSameColor) {
      setCurrentVariant(speedWithSameColor)
    } else {
      setCurrentVariant(variantsWithSameSpeed[0])
    }
  }

  return {
    currentVariant,
    speed,
    color,
    onColorChange,
    onSpeedChange,
  }
}
